import { ReactElement, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useWatch } from 'react-hook-form'
import { filter, find, isNull, isUndefined } from 'lodash-es'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Tab, styled } from '@mui/material'
import HotDimensioningCheckSettingsFormFields from '@editor/components/CheckSettingsFormFields/HotDimensioningCheckSettingsFormFields'
import ShearCheckSettingsFormFields from '@editor/components/CheckSettingsFormFields/ShearCheckSettingsFormFields'
import SteelBasePlateConfigFormFields from '@editor/components/CheckSettingsFormFields/SteelBasePlateConfigFormFields'
import VibrationCheckSettingsFormFields from '@editor/components/CheckSettingsFormFields/VibrationCheckSettingsFormFields'
import type { TabsConfig } from '@editor/components/CheckSettingsFormFields/config'
import {
  isHotDimensioningVisible,
  isShearForceReductionVisible,
  isSteelSupportVisible,
  isTimberSupportVisible,
  isVibrationVisible,
} from '@editor/components/CheckSettingsFormFields/utils'
import { EmptyState } from '@ui2/feedback'
import { GenericErrorBoundaryFallback } from '@ui/errors'

const StyledTabPanel = styled(TabPanel)({
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: 0,
  paddingBottom: 0,
})

type Props = {
  elementType: ElementTypes
  supportsGrid: ReactElement
  onActiveTabKeyChange?: (key: string | null) => void
}

const Results2CheckSettingsTabs = ({
  elementType,
  supportsGrid,
  onActiveTabKeyChange,
}: Props): ReactElement => {
  const rootPath = 'elements.0'
  const memberCheckSettingsSelectedPath = `${rootPath}.memberCheckSettingsSelectedElement`
  const memberCheckSettingsSelectedElement = useWatch({
    name: memberCheckSettingsSelectedPath,
  }) as SettingsOnMember | null | undefined

  const crossSection = useWatch({
    name: `${rootPath}.crossSection`,
  }) as CrossSectionTimberOrCLTOrSteel | undefined

  const elementCSisSteel =
    crossSection != null && 'material' in crossSection && crossSection.material?.kind === 'Steel'

  const hasHotDimensioningSettings =
    memberCheckSettingsSelectedElement != null &&
    'hot_dimensioning_settings' in memberCheckSettingsSelectedElement
  const hasShearCheckSettings =
    memberCheckSettingsSelectedElement != null &&
    'shear_check_settings' in memberCheckSettingsSelectedElement
  const hasSteelSupportConfig =
    memberCheckSettingsSelectedElement != null &&
    'steel_support_config' in memberCheckSettingsSelectedElement
  const hasVibrationCheckSettings =
    memberCheckSettingsSelectedElement != null &&
    'vibration_check_settings' in memberCheckSettingsSelectedElement

  const hasSupportConfigs =
    memberCheckSettingsSelectedElement != null &&
    'support_configs' in memberCheckSettingsSelectedElement

  const tabs: TabsConfig = useMemo(
    () => ({
      ap: {
        key: 'ap',
        label: 'Auflagerpressung',
        visible: hasSupportConfigs && isTimberSupportVisible(elementCSisSteel ? 'steel' : 'timber'),
      },
      hotDimensioning: {
        key: 'hotDimensioning',
        label: 'Heißbemessung',
        visible:
          hasHotDimensioningSettings &&
          isHotDimensioningVisible(elementType, elementCSisSteel ? 'steel' : 'timber'),
      },
      vibration: {
        key: 'vibration',
        label: 'Schwingung',
        visible:
          hasVibrationCheckSettings &&
          isVibrationVisible(elementType, elementCSisSteel ? 'steel' : 'timber'),
      },
      steelConfig: {
        key: 'steelConfig',
        label: 'Fußpunkt',
        visible:
          hasSteelSupportConfig &&
          isSteelSupportVisible(elementType, elementCSisSteel ? 'steel' : 'timber'),
      },
      shearCheck: {
        key: 'shearCheck',
        label: 'Querkraftabminderung',
        visible:
          hasShearCheckSettings &&
          isShearForceReductionVisible(elementType, elementCSisSteel ? 'steel' : 'timber'),
      },
    }),
    [
      elementCSisSteel,
      elementType,
      hasHotDimensioningSettings,
      hasShearCheckSettings,
      hasSteelSupportConfig,
      hasSupportConfigs,
      hasVibrationCheckSettings,
    ],
  )

  const defaultTab = useMemo(() => {
    if (tabs.ap.visible) return tabs.ap.key

    const firstEnabled = find(tabs, tab => tab.visible)

    return firstEnabled?.key ?? null
  }, [tabs])

  const [tab, setTab] = useState<string | null>(null)

  const resolvedTab = useMemo(() => {
    if (tab && tabs[tab as keyof TabsConfig]?.visible) return tab
    return defaultTab
  }, [tab, tabs, defaultTab])

  useEffect(() => {
    setTab(prev => {
      if (prev && tabs[prev as keyof TabsConfig]?.visible) return prev
      return defaultTab
    })
  }, [tabs, defaultTab])

  useLayoutEffect(() => {
    onActiveTabKeyChange?.(resolvedTab)
  }, [resolvedTab, onActiveTabKeyChange])

  if (
    isUndefined(memberCheckSettingsSelectedElement) ||
    memberCheckSettingsSelectedElement === null ||
    isNull(resolvedTab) ||
    isNull(defaultTab)
  ) {
    return (
      <EmptyState sx={{ py: 1.5 }}>
        Keine Nachweiseinstellungen für dieses Element verfügbar
      </EmptyState>
    )
  }

  return (
    <TabContext value={resolvedTab}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <TabList
          onChange={(_e, v) => setTab(v)}
          sx={{
            minHeight: 32,
            '& .MuiTab-root': { minHeight: 32, textTransform: 'none', py: 0.5 },
          }}
        >
          {filter(Object.values(tabs), t => t.visible).map(t => (
            <Tab key={t.key} label={t.label} value={t.key} data-cy={`tab-${t.key}`} />
          ))}
        </TabList>
      </Box>
      {tabs.ap.visible ? (
        <StyledTabPanel value={tabs.ap.key}>
          <Box sx={{ pt: 0.5 }}>{supportsGrid}</Box>
        </StyledTabPanel>
      ) : null}

      {tabs.hotDimensioning.visible ? (
        <StyledTabPanel value={tabs.hotDimensioning.key}>
          <Box p={1}>
            <ErrorBoundary FallbackComponent={GenericErrorBoundaryFallback}>
              <HotDimensioningCheckSettingsFormFields
                memberCheckSettingsSelectedPath={memberCheckSettingsSelectedPath}
                compact
              />
            </ErrorBoundary>
          </Box>
        </StyledTabPanel>
      ) : null}
      {tabs.shearCheck.visible ? (
        <StyledTabPanel value={tabs.shearCheck.key}>
          <Box p={1}>
            <ErrorBoundary FallbackComponent={GenericErrorBoundaryFallback}>
              <ShearCheckSettingsFormFields
                memberCheckSettingsSelectedPath={memberCheckSettingsSelectedPath}
                compact
              />
            </ErrorBoundary>
          </Box>
        </StyledTabPanel>
      ) : null}
      {tabs.steelConfig.visible ? (
        <StyledTabPanel value={tabs.steelConfig.key}>
          <Box p={1}>
            <ErrorBoundary FallbackComponent={GenericErrorBoundaryFallback}>
              <SteelBasePlateConfigFormFields
                memberCheckSettingsSelectedPath={memberCheckSettingsSelectedPath}
                compact
              />
            </ErrorBoundary>
          </Box>
        </StyledTabPanel>
      ) : null}

      {tabs.vibration.visible ? (
        <StyledTabPanel value={tabs.vibration.key}>
          <Box p={1}>
            <ErrorBoundary FallbackComponent={GenericErrorBoundaryFallback}>
              <VibrationCheckSettingsFormFields
                memberCheckSettingsSelectedPath={memberCheckSettingsSelectedPath}
                compact
              />
            </ErrorBoundary>
          </Box>
        </StyledTabPanel>
      ) : null}
    </TabContext>
  )
}

export default Results2CheckSettingsTabs
